/**
 * Move, Push Emojis, and Edit Row dialogs. Uses AdminContext for state and handlers.
 */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useAdminContext } from './AdminContext';

export default function PageDialogs() {
  const ctx = useAdminContext();
  const {
    editingRow,
    setEditingRow,
    setOriginalEditingRow,
    setEditFieldMetadata,
    setIsSaving,
    handleTableSave,
    activeTab,
    editFieldMetadata,
    isSaving,
    moveDialogOpen,
    setMoveDialogOpen,
    itemToMove,
    setItemToMove,
    moveTargetOptions,
    selectedMoveTarget,
    setSelectedMoveTarget,
    isMoving,
    handleConfirmMove,
    showPushEmojisDialog,
    setShowPushEmojisDialog,
    uploadedImageNames,
    setUploadedImageNames,
    setSelectedImages,
    setIsUploadingImages,
    isPushingEmojis,
    handlePushEmojisAfterUpload,
  } = ctx;

  const closeEditDialog = () => {
    setEditingRow(null);
    setOriginalEditingRow(null);
    setEditFieldMetadata([]);
    setIsSaving(false);
  };

  const closeMoveDialog = () => {
    setMoveDialogOpen(false);
    setItemToMove(null);
    setSelectedMoveTarget('');
  };

  const closePushEmojisDialog = () => {
    setShowPushEmojisDialog(false);
    setUploadedImageNames([]);
    setSelectedImages([]);
    setIsUploadingImages(false);
  };

  return (
    <>
      {editingRow && activeTab && (
        <Dialog open={!!editingRow} onClose={closeEditDialog} maxWidth="md" fullWidth>
          <DialogTitle>Edit {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</DialogTitle>
          <DialogContent>
            {(editFieldMetadata || []).map((field: { key: string; label: string }) => {
              const value = editingRow?.[field.key] ?? '';
              const isMultiline = typeof value === 'string' && value.length > 50;
              const fieldKey = field.key;
              const booleanFields = ['selectable_flair', 'selectable_inline'];
              const isBooleanField = booleanFields.includes(fieldKey);
              const boolValue = isBooleanField ? (value === 1 || value === true || value === '1') : null;

              if (isBooleanField) {
                return (
                  <FormControlLabel
                    key={field.key}
                    control={
                      <Switch
                        checked={!!boolValue}
                        onChange={(e) => {
                          setEditingRow((prev: Record<string, unknown> | null) => {
                            if (!prev) return prev;
                            return { ...prev, [fieldKey]: e.target.checked ? 1 : 0 };
                          });
                        }}
                      />
                    }
                    label={field.label}
                    sx={{ mt: 1, mb: 1 }}
                  />
                );
              }

              if (activeTab === 'class' && fieldKey === 'flair_height') {
                return null;
              }
              if (activeTab === 'class' && fieldKey === 'flair_width') {
                const h = Number(editingRow?.flair_height ?? 60);
                const flairSizeValue = h === 40 ? '60x40' : '60x60';
                return (
                  <FormControl key={field.key} fullWidth margin="normal">
                    <InputLabel id="edit-class-flair-size-label" shrink>Flair size</InputLabel>
                    <Select
                      labelId="edit-class-flair-size-label"
                      value={flairSizeValue}
                      label="Flair size"
                      onChange={(e) => {
                        const v = e.target.value as string;
                        const newH = v === '60x40' ? 40 : 60;
                        setEditingRow((prev: Record<string, unknown> | null) => {
                          if (!prev) return prev;
                          return { ...prev, flair_width: 60, flair_height: newH };
                        });
                      }}
                    >
                      <MenuItem value="60x60">60×60 (square)</MenuItem>
                      <MenuItem value="60x40">60×40 (wide)</MenuItem>
                    </Select>
                  </FormControl>
                );
              }

              return (
                <TextField
                  key={field.key}
                  fullWidth
                  margin="normal"
                  label={field.label}
                  value={value}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setEditingRow((prev: Record<string, unknown> | null) => {
                      if (!prev || prev[fieldKey] === newValue) return prev;
                      return { ...prev, [fieldKey]: newValue };
                    });
                  }}
                  multiline={isMultiline}
                />
              );
            })}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeEditDialog}>Cancel</Button>
            <Button
              onClick={handleTableSave}
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog open={moveDialogOpen} onClose={closeMoveDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Move {itemToMove?.type}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Move {itemToMove?.type === 'flair' ? 'flair' : itemToMove?.type === 'genus' ? 'genus' : 'family'}{' '}
            <strong>{itemToMove?.name}</strong> to a different{' '}
            {itemToMove?.type === 'flair' ? 'genus' : itemToMove?.type === 'genus' ? 'family' : 'class'}:
          </Typography>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="move-target-label" shrink sx={{ backgroundColor: 'background.paper', px: 1 }}>
              {`Select ${itemToMove?.type === 'flair' ? 'Genus' : itemToMove?.type === 'genus' ? 'Family' : 'Class'}`}
            </InputLabel>
            <Select
              labelId="move-target-label"
              value={selectedMoveTarget || ''}
              onChange={(e) => setSelectedMoveTarget(e.target.value)}
              native
              label={`Select ${itemToMove?.type === 'flair' ? 'Genus' : itemToMove?.type === 'genus' ? 'Family' : 'Class'}`}
              sx={{ '& select': { paddingTop: '16.5px', paddingBottom: '16.5px' } }}
            >
              <option value="" disabled>{`-- Select ${itemToMove?.type === 'flair' ? 'Genus' : itemToMove?.type === 'genus' ? 'Family' : 'Class'} --`}</option>
              {(moveTargetOptions || []).map((option: Record<string, unknown>, i: number) => {
                const targetParentType =
                  itemToMove?.type === 'flair' ? 'genus' : itemToMove?.type === 'genus' ? 'family' : 'class';
                const nameColumn = `${targetParentType}_name`;
                const idColumn = `${targetParentType}_id`;
                const displayName = (option[nameColumn] || option[idColumn] || 'Unknown') as string;
                const id = option[idColumn] || option.id;
                return (
                  <option key={String(id ?? i)} value={String(id)}>
                    {displayName}
                  </option>
                );
              })}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMoveDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmMove}
            variant="contained"
            color="primary"
            disabled={!selectedMoveTarget || isMoving}
          >
            {isMoving ? 'Moving...' : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showPushEmojisDialog} onClose={closePushEmojisDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Push Emojis to Reddit?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Would you like to push the {(uploadedImageNames || []).length} uploaded image{(uploadedImageNames || []).length !== 1 ? 's' : ''} as emojis to Reddit?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Images: {(uploadedImageNames || []).slice(0, 5).join(', ')}
            {(uploadedImageNames || []).length > 5 ? ` ... and ${(uploadedImageNames || []).length - 5} more` : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePushEmojisDialog}>Skip</Button>
          <Button
            onClick={handlePushEmojisAfterUpload}
            variant="contained"
            color="primary"
            disabled={isPushingEmojis}
          >
            {isPushingEmojis ? 'Pushing...' : 'Push Emojis'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
